/* ==========================================================================
   LADDOM — MOTION ENGINE (Module 04)
   Singleton window.Motion. No section registers its own ScrollTrigger.
   Reduced-motion is evaluated FIRST and re-evaluated live.
   ========================================================================== */
(function () {
  'use strict';

  // Every element the reveal engine drives. Kept in one place so a new
  // contract (the media settle) cannot be added to the observer and forgotten
  // in the reduced-motion fallbacks, which would leave content invisible.
  var REVEAL_SEL = '[data-reveal], [data-reveal-clip], [data-reveal-media]';

  var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  var coarse = window.matchMedia('(pointer: coarse)');
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  // Evaluated on every call, not captured once: GSAP and ScrollTrigger are now
  // fetched on demand, so "absent at parse time" no longer means "absent".
  function hasGsap() { return typeof window.gsap !== 'undefined'; }
  function hasST() { return hasGsap() && typeof window.ScrollTrigger !== 'undefined'; }

  // root -> { observers: [], triggers: [] }
  var owned = new Map();

  function bucket(root) {
    if (!owned.has(root)) owned.set(root, { observers: [], triggers: [] });
    return owned.get(root);
  }

  var Motion = {
    reduced: mq.matches,
    lenis: null,
    initialised: false,
    _queue: [],
    _refreshTimer: null,
  };

  /* ---------------------------------------------------------------- REVEAL
     IntersectionObserver, never ScrollTrigger. This is the workhorse and it
     must be nearly free. Unobserves after firing so nothing re-animates. */
  Motion.reveal = function (root) {
    root = root || document;
    var nodes = root.querySelectorAll(REVEAL_SEL);
    if (!nodes.length) return;

    if (Motion.reduced) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );

    nodes.forEach(function (n) {
      if (n.getAttribute('data-reveal') === 'stagger') {
        // Cap accumulation at 6 — beyond that the last child feels late.
        Array.prototype.slice.call(n.children).forEach(function (child, i) {
          child.style.setProperty('--i', Math.min(i, 5));
        });
      }
      io.observe(n);
    });

    bucket(root).observers.push(io);
  };

  /* ------------------------------------------------- ON-DEMAND MOTION VENDORS
     GSAP + ScrollTrigger are 44.9kb gzipped and drive exactly one feature:
     [data-parallax]. Shipping them on every template blew the JS budget for no
     visible effect. They are fetched the first time a parallax element is
     genuinely on the page, and never otherwise.

     Failure is non-fatal by design: parallax is decoration, and the media it
     would move is already visible and laid out. A blocked or failed vendor
     request must never leave content stuck invisible. */
  var vendorState = 'idle'; // idle | loading | ready | failed
  var vendorWaiters = [];

  function vendorUrls() {
    var el = document.getElementById('ld-motion-vendors');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = false; // preserve gsap-before-scrolltrigger order
      s.onload = resolve;
      s.onerror = function () { reject(new Error('failed: ' + src)); };
      document.head.appendChild(s);
    });
  }

  function flushVendors(ok) {
    var waiting = vendorWaiters;
    vendorWaiters = [];
    waiting.forEach(function (fn) { fn(ok); });
  }

  function ensureVendors(cb) {
    if (hasST()) { cb(true); return; }
    if (vendorState === 'failed') { cb(false); return; }
    vendorWaiters.push(cb);
    if (vendorState === 'loading') return;

    var urls = vendorUrls();
    if (!urls || !urls.gsap || !urls.scrolltrigger) {
      vendorState = 'failed';
      flushVendors(false);
      return;
    }
    vendorState = 'loading';
    loadScript(urls.gsap)
      .then(function () { return loadScript(urls.scrolltrigger); })
      .then(function () {
        vendorState = 'ready';
        // Lenis may already be running on its own rAF loop. Now that
        // ScrollTrigger exists, hand it the scroll signal it needs.
        if (Motion.lenis && hasST()) {
          Motion.lenis.on('scroll', window.ScrollTrigger.update);
        }
        flushVendors(true);
      })
      .catch(function () {
        vendorState = 'failed';
        flushVendors(false);
      });
  }

  /* -------------------------------------------------------------- PARALLAX
     Transform only. Never background-position. Desktop only. */
  Motion.parallax = function (root) {
    root = root || document;
    if (Motion.reduced || window.innerWidth < 768) return;
    if (!root.querySelectorAll('[data-parallax]').length) return;

    ensureVendors(function (ok) {
      // Re-check: the user may have switched on reduce-motion, or resized,
      // while the vendor request was in flight.
      if (!ok || Motion.reduced || window.innerWidth < 768) return;
      bindParallax(root);
    });
  };

  function bindParallax(root) {
    root.querySelectorAll('[data-parallax]').forEach(function (el) {
      var amount = parseFloat(el.getAttribute('data-parallax')) || 0.15;
      if (amount > 0.25) amount = 0.25; // past this the image edges reveal
      var t = window.gsap.to(el, {
        yPercent: amount * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.6, // the slight lag is what reads as weight
        },
      });
      if (t.scrollTrigger) bucket(root).triggers.push(t.scrollTrigger);
    });
  }

  /* Pin has no callers in the theme today. It stays because it is part of the
     Motion contract, but it can no longer assume ScrollTrigger is present, so
     it resolves asynchronously and reports the trigger through a callback
     instead of returning it. */
  Motion.pin = function (el, opts, done) {
    if (Motion.reduced || window.innerWidth < 990) return;
    ensureVendors(function (ok) {
      if (!ok || Motion.reduced || window.innerWidth < 990) return;
      var st = window.ScrollTrigger.create(
        Object.assign({ trigger: el, pin: true, pinSpacing: true }, opts || {})
      );
      bucket(document).triggers.push(st);
      if (typeof done === 'function') done(st);
    });
  };

  Motion.refresh = function () {
    if (!hasST()) return;
    clearTimeout(Motion._refreshTimer);
    Motion._refreshTimer = setTimeout(function () {
      window.ScrollTrigger.refresh();
    }, 100);
  };

  Motion.destroy = function (root) {
    root = root || document;
    var b = owned.get(root);
    if (!b) return;
    b.observers.forEach(function (o) { o.disconnect(); });
    b.triggers.forEach(function (t) { t.kill(); });
    owned.delete(root);
  };

  Motion.register = function (fn) {
    Motion._queue.push(fn);
    if (Motion.initialised) fn(document);
  };

  /* ----------------------------------------------------------------- LENIS
     Only when: not reduced, not touch-primary, not in the theme editor.

     Preferably driven from GSAP's ticker, because a single ticker keeps smooth
     scroll and any scroll-triggered animation on the same clock. GSAP is no
     longer loaded on every page though, so Lenis falls back to its own rAF
     loop. Requiring GSAP here would have meant no smooth scroll at all on the
     pages that do not use parallax — which is most of them. */
  var lenisRaf = null;

  function lenisTick(t) {
    if (Motion.lenis) Motion.lenis.raf(t * 1000);
  }

  function lenisRafLoop(t) {
    if (!Motion.lenis) return;
    Motion.lenis.raf(t);
    lenisRaf = requestAnimationFrame(lenisRafLoop);
  }

  function initLenis() {
    if (Motion.reduced || coarse.matches || designMode) return;
    if (typeof window.Lenis === 'undefined' || Motion.lenis) return;

    Motion.lenis = new window.Lenis({
      lerp: 0.085,
      wheelMultiplier: 1,
      smoothWheel: true,
      syncTouch: false,
    });

    if (hasST()) Motion.lenis.on('scroll', window.ScrollTrigger.update);

    if (hasGsap()) {
      window.gsap.ticker.add(lenisTick);
      window.gsap.ticker.lagSmoothing(0);
    } else {
      lenisRaf = requestAnimationFrame(lenisRafLoop);
    }
  }

  function teardownLenis() {
    if (!Motion.lenis) return;
    if (lenisRaf) {
      cancelAnimationFrame(lenisRaf);
      lenisRaf = null;
    }
    if (hasGsap() && window.gsap.ticker) window.gsap.ticker.remove(lenisTick);
    Motion.lenis.destroy();
    Motion.lenis = null;
  }

  /* ------------------------------------------------------------------ INIT */
  function init() {
    if (!Motion.reduced) initLenis();
    Motion.reveal(document);
    Motion.parallax(document);
    Motion._queue.forEach(function (fn) { fn(document); });
    Motion.initialised = true;
  }

  // Live reduced-motion changes. Never leave a reduce-motion user with
  // invisible content.
  function onReducedChange() {
    Motion.reduced = mq.matches;
    if (Motion.reduced) {
      teardownLenis();
      document
        .querySelectorAll(REVEAL_SEL)
        .forEach(function (n) { n.classList.add('is-in'); });
      owned.forEach(function (b) { b.triggers.forEach(function (t) { t.kill(); }); });
    } else {
      initLenis();
      Motion.parallax(document);
      Motion.refresh();
    }
  }
  if (mq.addEventListener) mq.addEventListener('change', onReducedChange);
  else if (mq.addListener) mq.addListener(onReducedChange);

  /* --------------------------------------------------- THEME EDITOR SURVIVAL
     Mandatory. Without this the merchant cannot edit the theme. */
  document.addEventListener('shopify:section:load', function (e) {
    Motion.destroy(e.target);
    Motion.reveal(e.target);
    Motion.parallax(e.target);
    Motion._queue.forEach(function (fn) { fn(e.target); });
    Motion.refresh();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    Motion.destroy(e.target);
    Motion.refresh();
  });

  document.addEventListener('shopify:block:select', function (e) {
    if (Motion.lenis) Motion.lenis.scrollTo(e.target, { offset: -120 });
    else e.target.scrollIntoView({ block: 'center' });
  });

  window.Motion = Motion;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
